import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, getStoredLanguage, normalizeLanguage, setStoredLanguage } from '../utils/locale';
import { translations } from './translations';

const LanguageContext = createContext(null);

const resolveTranslation = (language, key) => {
  const selected = translations[language] || translations.en;
  const fallback = translations.en || {};
  const path = key.split('.');
  const pick = (source) => path.reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), source);
  return pick(selected) ?? pick(fallback);
};

const interpolate = (value, params) => {
  if (typeof value !== 'string' || !params) {
    return value;
  }
  return Object.keys(params).reduce((text, key) => {
    return text.replace(new RegExp(`\\{${key}\\}`, 'g'), String(params[key]));
  }, value);
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => getStoredLanguage());

  useEffect(() => {
    setStoredLanguage(language);
  }, [language]);

  useEffect(() => {
    const handleStorage = () => {
      setLanguageState(getStoredLanguage());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState(normalizeLanguage(nextLanguage));
  }, []);

  const t = useCallback(
    (key, params) => {
      const resolved = resolveTranslation(language, key);
      if (!resolved) {
        return key;
      }
      return interpolate(resolved, params) || key;
    },
    [language]
  );

  const value = useMemo(() => ({
    language,
    locale: getLocale(language),
    setLanguage,
    t
  }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
